import { ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import {
  LoadingContextProvider,
  NotificationContextProvider,
  ChatVisibilityProvider,
  ChatVisibilityHandler,
} from "cerulean";

import type { PreloadedState } from "byzantine";
import AppLocalizationProvider, {
  AppLocalizationProviderProps,
} from "../AppLocalizationProvider";
import {
  CurrentUserContextProvider,
  CurrentUserContextProviderProps,
} from "../contexts/CurrentUserContext";
import { UserFeaturesContextProvider } from "../contexts/UserFeaturesContext";
import { LimitsContextProvider } from "../contexts/LimitsContext";

import DBBLLibraryProvider from "../../src/libraries/library.provider";
import { InstitutionSettingsContextProvider } from "../contexts/InstitutionSettingsContext";
import { OptionalFingerprintProvider } from "../fingerprint/OptionalFingerprintProvider";

interface AppProvidersProps
  extends AppLocalizationProviderProps,
    CurrentUserContextProviderProps {
  preloadedState: PreloadedState;
  children: ReactNode;
}

const queryClient = new QueryClient();
const ReactQueryProvider = ({ children }: { children: React.ReactNode }) => (
  <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
);

const AppProviders = ({
  currentUser,
  internalName,
  s3ImagesBucket,
  fingerprintApiKey,
  fingerprintEndpoint,
  fingerprintScriptSrc,
  deviceMonitoringEnabled,
  preloadedState,
  isHiddenWhileLoading,
  children,
}: AppProvidersProps) => (
  <AppLocalizationProvider
    internalName={internalName}
    s3ImagesBucket={s3ImagesBucket}
    fingerprintApiKey={fingerprintApiKey}
    fingerprintEndpoint={fingerprintEndpoint}
    fingerprintScriptSrc={fingerprintScriptSrc}
    deviceMonitoringEnabled={deviceMonitoringEnabled}
    isHiddenWhileLoading={isHiddenWhileLoading}
  >
    <ReactQueryProvider>
      <NotificationContextProvider>
        <LoadingContextProvider>
          <UserFeaturesContextProvider>
            <CurrentUserContextProvider currentUser={currentUser}>
              <LimitsContextProvider>
                <DBBLLibraryProvider preloadedState={preloadedState}>
                  <InstitutionSettingsContextProvider>
                    <OptionalFingerprintProvider
                      fingerprintApiKey={fingerprintApiKey}
                      fingerprintEndpoint={fingerprintEndpoint}
                      fingerprintScriptSrc={fingerprintScriptSrc}
                      deviceMonitoringEnabled={deviceMonitoringEnabled}
                    >
                      <ChatVisibilityProvider>
                        <ChatVisibilityHandler />
                        {children}
                      </ChatVisibilityProvider>
                    </OptionalFingerprintProvider>
                  </InstitutionSettingsContextProvider>
                </DBBLLibraryProvider>
              </LimitsContextProvider>
            </CurrentUserContextProvider>
          </UserFeaturesContextProvider>
        </LoadingContextProvider>
      </NotificationContextProvider>
    </ReactQueryProvider>
  </AppLocalizationProvider>
);

export default AppProviders;
